import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Avatar,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { getAllTechnicianStops } from '@/features/scheduling/api/scheduling';

const iso = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
export function FoundationPage() {
  const [date, setDate] = useState(new Date());
  const query = useQuery({
    queryKey: ['mission-control', iso(date)],
    queryFn: () => getAllTechnicianStops(iso(date)),
  });
  const rows = (query.data ?? []).filter((day) => day.route_stops_count > 0);
  const shift = (days: number) =>
    setDate((old) => new Date(old.getFullYear(), old.getMonth(), old.getDate() + days));
  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={0} sx={paperSx}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          gap={2}
          sx={{ p: 2 }}
        >
          <Box>
            <Typography variant="h1">Mission Control</Typography>
            <Typography color="text.secondary">
              Manage and track your technicians' routes.
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center">
            <Button aria-label="Refresh" sx={commandButtonSx} onClick={() => void query.refetch()}>
              <RefreshIcon />
            </Button>
            <Button aria-label="Previous day" sx={commandButtonSx} onClick={() => shift(-1)}>
              <ChevronLeftIcon />
            </Button>
            <TextField
              type="date"
              size="small"
              value={iso(date)}
              inputProps={{ 'aria-label': 'Route date' }}
              onChange={(event) => setDate(new Date(`${event.target.value}T12:00:00`))}
            />
            <Button sx={commandButtonSx} onClick={() => setDate(new Date())}>
              Today
            </Button>
            <Button aria-label="Next day" sx={commandButtonSx} onClick={() => shift(1)}>
              <ChevronRightIcon />
            </Button>
          </Stack>
        </Stack>
      </Paper>
      {query.isFetching && <LinearProgress />}
      {query.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Route information could not be loaded.
        </Alert>
      )}
      <Paper
        elevation={0}
        sx={{
          ...paperSx,
          mt: 1.5,
          px: 2,
          py: 1.75,
          borderTop: '3px solid',
          borderTopColor: 'primary.main',
        }}
      >
        <Typography color="primary.main">
          {date.toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </Typography>
      </Paper>
      <Stack spacing={1.5} sx={{ mt: 1.5 }}>
        {rows.map((day) => {
          const done = day.route_stops.filter((stop) => stop.service_report_completed).length;
          return (
            <Paper
              key={day.technician.id}
              component={Link}
              to={`/foundation/technicians/${day.technician.id}?date=${iso(date)}`}
              elevation={0}
              sx={{
                ...paperSx,
                p: 2,
                display: 'block',
                color: 'text.primary',
                textDecoration: 'none',
                transition: 'background-color .15s ease, box-shadow .15s ease',
                '&:hover': { bgcolor: '#f8fbff', boxShadow: '0 3px 9px rgba(0,0,0,.16)' },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            >
              <Stack direction="row" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'primary.main', color: '#fff' }}>
                  {day.technician.name
                    .split(/\s+/)
                    .map((part) => part[0])
                    .join('')
                    .slice(0, 2)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h2">{day.technician.name}</Typography>
                  <Typography color="text.secondary">
                    {done}/{day.route_stops_count} stops complete
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          );
        })}
        {!query.isLoading && rows.length === 0 && (
          <Alert severity="info">No technician routes have been scheduled for this day.</Alert>
        )}
      </Stack>
    </Box>
  );
}
const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
const commandButtonSx = { color: 'text.primary' };
